import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Yup from 'yup';

import { Grid, Tab, Tabs } from '@material-ui/core';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { BiSearchAlt } from 'react-icons/bi';
import { FaEdit } from 'react-icons/fa';

import { AxiosError } from 'axios';
import { Link } from 'react-router-dom';
import Button from '../../../../../components/Button';
import Input from '../../../../../components/Input';
import PageTitle from '../../../../../components/PageTitle';
import { IUser, useAuth } from '../../../../../hooks/auth';
import { useToast } from '../../../../../hooks/toast';

import api from '../../../../../services/api';
import getValidationErrors from '../../../../../utils/getValidationsErrors';

import Select from '../../../../../components/Select';
import * as S from './styles';

import { FiAlertTriangle } from 'react-icons/fi';
import { HiOutlineTrash } from 'react-icons/hi';
import Map, { ILocal } from '../../../../../components/Map';
import { MultiSelect } from '../../../../../components/MultiSelectChip';
import Slider from '../../../../../components/Slider';
import { IDevice } from '../../../../../interfaces';
import { IClient } from '../../../../../interfaces/IClient';


const versionOptions = [
  { id: '1', name: 'Default' },
  { id: '2', name: 'Multiple' },
  { id: '3', name: 'Ayyeka' },
  { id: '4', name: 'SW8 PRO' }
];

const Devices: React.FC = () => {

  const deviceFormRef = useRef<FormHandles>(null);
  const searchDeviceFormRef = useRef<FormHandles>(null);
  const { user: authUser } = useAuth();

  const { addToast } = useToast();

  const [deleteDeviceId, setDeleteDeviceId] = useState('');
  const [loader, setLoader] = useState(false);
  const [usersForDeviceList, setUsersForDeviceList] = useState<IUser[]>([] as IUser[]);
  const [deviceList, setDeviceList] = useState<IDevice[]>([] as IDevice[]);
  const [batteryEmailAlert, setBatteryEmailAlert] = useState(false);
  const [batteryAlert, setBatteryAlert] = useState<boolean>(true);
  const [clientList, setClientList] = useState<IClient[]>([] as IClient[]);
  const [selectedUsers, setSelectedUsers] = useState<{ id: string, name: string }[]>([] as { id: string, name: string }[]);
  const [tabValue, setTabValue] = useState(0);
  const isClientAdmin = authUser.sysadmin || authUser.client_admin
  const [locals, setLocals] = useState<ILocal[]>([] as ILocal[]);

  const canCreate: boolean = authUser.sysadmin || (authUser.permissions?.includes("|device-create|") || false);
  const canDelete: boolean = authUser.sysadmin || (authUser.permissions?.includes("|device-delete|") || false);

  const getClientList = useCallback(async () => {
    try {
      const { data } = await api.get('clients');
      return setClientList(data);
    } catch (error) {
      return addToast({
        type: 'error',
        title: 'Erro ao buscar Clientes',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  }, [addToast]);

  const handleSelectChange = (selectedOptions: any) => {
    let users = selectedOptions.map((u: { value: any, label: any }) => ({ id: u.value, name: u.label }));
    setSelectedUsers(users);
  };

  const searchUsersForDeviceList = useCallback(async (client_id: string | undefined = undefined) => {
    //const searchUser = deviceFormRef.current?.getFieldValue('user');

    if (client_id === undefined && !isClientAdmin) {
      client_id = authUser.client_id;
    }

    try {
      const { data } = await api.get('profiles', { params: { client_id } });

      if (authUser.permissions?.includes('|users-managers|')) {
        return setUsersForDeviceList([authUser, ...data]);
      }

      return setUsersForDeviceList(data);
    } catch (error) {
      return addToast({
        type: 'error',
        title: 'Erro ao buscar usuários',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  }, [addToast, authUser, isClientAdmin]);

  const searchDeviceHandle = useCallback(async () => {
    const name = searchDeviceFormRef.current?.getFieldValue('searchDevice');
    let client_id = undefined
    try {

      if (isClientAdmin) {
        client_id = searchDeviceFormRef.current?.getFieldValue('searchClient');
      }
      const { data } = await api.get('devices', { params: { name, client_id } });

      if (data.length > 0) {
        const deviceLocals: ILocal[] = [];

        data.map((device: IDevice) => {
          let latitude = device.latitude_iot || device.latitude;
          let longitude = device.longitude_iot || device.longitude;
          if (latitude && longitude) {
            deviceLocals.push({
              coords: [
                latitude,
                longitude,
              ],
              popup: device.name,
            });
          }
        });
        setLocals(deviceLocals);
      } else {
        setLocals([]);
      }

      setDeviceList(data);
    } catch (error) {
      return addToast({
        type: 'error',
        title: 'Erro ao buscar dispositivos',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  }, [addToast, authUser, isClientAdmin]);

  const onDeleteDevice = async () => {
    try {
      await api.delete('devices', { params: { id: deleteDeviceId } });

      setDeleteDeviceId('');

      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'O dispositivo foi excluído com sucesso',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao excluir dispositivo',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  };

  const deviceFormHandler = useCallback(
    async formData => {
      setLoader(true);

      try {
        deviceFormRef.current?.setErrors({});

        let fieldToValidate: any = {
          name: Yup.string().required(),
          mac: Yup.string().required(),
          version: Yup.string().required(),
          battery_alert: Yup.boolean().required(),
          battery_email_alert: Yup.boolean().when('battery_alert', {
            is: true,
            then: Yup.boolean().required(),
            otherwise: Yup.boolean().optional(),
          }),
          battery_alert_limit: Yup.number()
            .transform((value, originalValue) => (originalValue.trim() === '' ? null : value))
            .when('battery_alert', {
              is: true,
              then: Yup.number().required(),
              otherwise: Yup.number().nullable(),
            }),
          multiSelectUsers: Yup.array().required("You must select at least one user"),
        };

        if (isClientAdmin) {
          fieldToValidate = {
            ...fieldToValidate,
            client: Yup.string().required(),
          }
        }

        formData.multiSelectUsers = selectedUsers || [];

        const schema = Yup.object().shape(fieldToValidate);

        await schema.validate(formData, {
          abortEarly: false,
        });

        const { name, mac, version, battery_alert, battery_email_alert, battery_alert_limit, client } = formData;
        const dataToSend = {
          name,
          version,
          users: formData.multiSelectUsers,
          mac_address: mac,
          battery_alert,
          battery_email_alert: battery_alert ? battery_email_alert : undefined,
          battery_alert_limit: battery_alert ? battery_alert_limit : undefined,
          client_id: client || authUser.client_id
        };

        await api.post('/devices', dataToSend);

        deviceFormRef.current?.reset();

        addToast({
          type: 'success',
          title: 'Dispositivo criado!',
          description: 'Dispositivo criado com sucesso!',
        });

        setUsersForDeviceList([]);

        return searchDeviceHandle();
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          return deviceFormRef.current?.setErrors(errors);
        }
        return addToast({
          type: 'error',
          title: 'Erro ao criar novo dispositivo',
          description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
        });
      } finally {
        searchDeviceHandle();

        setLoader(false);
      }
    },
    [addToast, searchDeviceHandle, selectedUsers, authUser, isClientAdmin],
  );

  useEffect(() => {
    searchDeviceHandle();
    if (isClientAdmin) {
      getClientList();
    } else {
      searchUsersForDeviceList();
    }
  }, [searchDeviceHandle, searchUsersForDeviceList, deleteDeviceId, getClientList, selectedUsers, isClientAdmin]);

  const deviceForm = useMemo(() => {
    return (
      <Form ref={deviceFormRef} onSubmit={deviceFormHandler}>
        <h2>Criar novo dispositivo</h2>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} className={loader ? 'disabled' : ''}>
            <p>Nome</p>
            <Input hideErrorIcon name="name" type="text" />
          </Grid>
          {isClientAdmin && (
            <Grid item xs={12}>
              <p>Selecione um cliente</p>
              <Select name="client" data={clientList} onChange={(event) => searchUsersForDeviceList(event.target.value)} />
            </Grid>
          )}
          <Grid item xs={12}>
            <p>Selecione um usuário</p>
            <MultiSelect placeholder="" name="multiSelectUsers" onChange={handleSelectChange} options={usersForDeviceList.map(u => ({ value: u.id, label: u.name }))}></MultiSelect>
          </Grid>
          <Grid item xs={12} className={loader ? 'disabled' : ''}>
            <p>Versão</p>
            <Select
              name="version"
              disabled={loader}
              data={versionOptions}
            />
          </Grid>
          <Grid item xs={12} className={loader ? 'disabled' : ''}>
            <p>Endereço (mac do dispositivo)</p>
            <Input hideErrorIcon name="mac" type="text" className="mac" />
          </Grid>
        </Grid>
        <Grid>
          <S.CheckContainer>
            <p>Alerta da bateria</p>
            <Slider
              name="battery_alert"
              checked={batteryAlert}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setBatteryAlert(e.target.checked);
              }}
            />
          </S.CheckContainer>
        </Grid>
        <Grid>
          <S.FormFieldsContainer height={batteryAlert ? 8 : 0}>
            <Grid item xs={12}>
              <p>Percentual limite de bateria</p>
              <Input name="battery_alert_limit" type="number" placeholder="15" showPercentage />
            </Grid>
            <Grid item xs={12}>
              <div className="slider-container">
                <p>Receber alerta por email</p>
                <Slider
                  name="battery_email_alert"
                  checked={batteryEmailAlert}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setBatteryEmailAlert(e.target.checked);
                  }}
                />
              </div>
            </Grid>
          </S.FormFieldsContainer>
        </Grid>
        <Grid>
          <Button className="submitNewDeviceButton">{loader ? 'Cadastrando ...' : 'Cadastrar'}</Button>
        </Grid>
      </Form>
    );
  }, [loader, deviceFormHandler, usersForDeviceList, batteryAlert, clientList]);

  const searchDeviceForm = useMemo(() => {
    return (
      <Form onSubmit={searchDeviceHandle} ref={searchDeviceFormRef} className="search">
        <h3>
          <BiSearchAlt />
          Buscar
          <span> dispositivos</span>
        </h3>

        <Input name="searchDevice" type="text" placeholder="Nome do dispositivo..." />
        {(isClientAdmin) && <Select name="searchClient" data={clientList} placeholder="Cliente..." />}
        <Button>pesquisar</Button>
      </Form>
    );
  }, [searchDeviceHandle, clientList, isClientAdmin]);

  const searchDevice = useMemo(() => {
    return (
      <div className="results">
        <Tabs value={tabValue} aria-label="display tabs for devices and maps"
          indicatorColor='primary'
          textColor='inherit'>
          <Tab label="TABELA" value={0} onClick={() => setTabValue(0)} />
          <Tab label="MAPA" value={1} onClick={() => setTabValue(1)} />
        </Tabs>
        <br />
        {tabValue === 0 && (
          <>
            <h3>Resultado:</h3>
            <ul>
              {deviceList.length > 0 ? (
                deviceList.map(device => (
                  <button type="button" key={device.id}>
                    <p>{device.name}</p>
                    {(isClientAdmin) && (<p>{device.client.name}</p>)}
                    <span>
                      <Link to={`/app/device/${device.id}`}>
                        <FaEdit />
                      </Link>
                    </span>
                    {canDelete && (
                      <span onClick={() => setDeleteDeviceId(device.id)}>
                        <HiOutlineTrash />
                      </span>
                    )}
                  </button>
                ))
              ) : (
                <h3 className="result">Nenhum dispositivo encontrado</h3>
              )}
            </ul>
          </>
        )}
        {tabValue === 1 && (<Map locals={locals} />)}

      </div>
    );
  }, [deviceList, setTabValue, tabValue, isClientAdmin, canDelete, locals]);

  return (
    <>
      {!deleteDeviceId && (
        <>
          <PageTitle sub="Dispositivos" title="Configurar" />
          <S.Container>
            <S.SearchDeviceForm show>{searchDeviceForm}</S.SearchDeviceForm>
            <S.SearchDevice show>{searchDevice}</S.SearchDevice>
            {canCreate && <S.AdminUpdateFormContainer>{deviceForm}</S.AdminUpdateFormContainer>}
          </S.Container>
        </>
      )}
      {!!deleteDeviceId && (
        <S.DeletePopup>
          <div>
            <FiAlertTriangle size={30} />
            <p>Deseja excluir o dispositivo?</p>
            <div className="buttons">
              <Button className="cancel" onClick={() => setDeleteDeviceId('')}>
                Cancelar
              </Button>
              <Button className="delete" onClick={onDeleteDevice}>
                Excluir
              </Button>
            </div>
          </div>
        </S.DeletePopup>
      )}
    </>
  );
};

export default Devices;
